import React, { useEffect, useMemo } from 'react';
import { Form as AntdForm } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { routes } from '../../../../../utils/helpers/ApiClient';
import AppBarCMS from '../../../../basic/Navigation/AppBarCMS/AppBarCMS';
import { AdminContentBlock, Spinner } from '../../../../common';
import Form from '../../../../antd/form/Form/Form';
import {
  useGetPhdStudyFieldsQuery,
  useSavePhdStudyFieldMutation,
} from '../../../../../redux/services/phd';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import { applyPhdValidationErrors } from '../utils/phdApiFeedback.js';
import { usePhdDirtyLeaveGuard } from '../usePhdDirtyLeaveGuard.jsx';
import { normalizeCollection } from './phdStudyFieldsHelpers.js';
import PhdStudyFieldsEditForm from './PhdStudyFieldsEditForm.jsx';
import PhdPageContext from '../../../../common/Phd/PhdPageContext.jsx';

const PhdStudyFieldsEdit = ({ mode }) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = AntdForm.useForm();
  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';
  const pageTitle = isCreate
    ? t('Nový študijný odbor')
    : t('Upraviť študijný odbor');

  useEffect(() => {
    document.title = `${pageTitle} - FRI CMS`;
  }, [pageTitle]);

  const { data: fieldsRaw, isLoading: isFieldsLoading } =
    useGetPhdStudyFieldsQuery();
  const [savePhdStudyField, { isLoading: isSaving }] =
    useSavePhdStudyFieldMutation();
  const { saveSuccess, saveFailed } = useToastMessages();

  const field = useMemo(
    () =>
      isEdit
        ? normalizeCollection(fieldsRaw).find(
            (item) => String(item.id) === String(id),
          )
        : null,
    [fieldsRaw, id, isEdit],
  );

  const initials = useMemo(() => {
    if (isCreate) {
      return { study_field: undefined, code: undefined, focus: undefined };
    }

    if (!field) {
      return null;
    }

    return {
      study_field: field.study_field,
      code: field.code,
      focus: field.focus,
    };
  }, [field, isCreate]);

  const { modal, onSaveFailed, onSaveSuccess, onValuesChange } =
    usePhdDirtyLeaveGuard(form, initials);

  const handleSubmit = async (values) => {
    try {
      await savePhdStudyField({
        id: isEdit ? field.id : undefined,
        body: {
          study_field: values.study_field,
          code: values.code,
          focus: values.focus || null,
        },
      }).unwrap();
      saveSuccess();
      onSaveSuccess(() => navigate(routes.cmsPhdStudyFields));
    } catch (error) {
      onSaveFailed();
      applyPhdValidationErrors(form, error);
      saveFailed(error);
    }
  };

  if (isFieldsLoading || (isEdit && !field) || !initials) {
    return <Spinner />;
  }

  return (
    <>
      {modal}
      <AppBarCMS
        title={pageTitle}
        backUrl={routes.cmsPhdStudyFields}
        backUrlTooltip={t('Študijné odbory a predmety')}
        permissionsBasic="phd-study-fields"
      />
      <div style={{ margin: '10px' }}>
        <PhdPageContext
          title={t('Ako fungujú študijné odbory a predmety?')}
          description={t(
            'Odbor sa používa pri doktorandoch, témach, pracovných skupinách a predmetoch. Po uložení priraďte odboru predmety v zozname číselníkov.',
          )}
        />
        <AdminContentBlock
          icon="fa fa-graduation-cap"
          title={pageTitle}
          link={routes.cmsPhdStudyFields}
        >
          <Form
            form={form}
            initialValues={initials}
            onFinish={handleSubmit}
            onValuesChange={onValuesChange}
          >
            <PhdStudyFieldsEditForm isSubmitting={isSaving} />
          </Form>
        </AdminContentBlock>
      </div>
    </>
  );
};

export default PhdStudyFieldsEdit;
